import {
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Button,
  Badge,
  Fade,
  Container,
  alpha,
  Divider,
  Collapse,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Tooltip,
  Rating,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import {
  PhotoCamera,
  Favorite,
  Share,
  Bookmark,
  BookmarkBorder,
  CalendarToday,
  Person,
  AccessTime,
  LocationOn,
  Grade,
  EmojiEmotions,
  Comment,
  Visibility,
  Download,
  Print,
  ExpandMore,
  ExpandLess,
  Palette,
  MusicNote,
  SportsSoccer,
  Science,
  Restaurant,
  Bedtime,
  LocalLibrary,
  TrendingUp,
  CheckCircle,
  Error,
  Warning,
  Info,
  ThumbUp,
  CloudUpload,
  Image,
  Description,
  Schedule,
  Group,
  Psychology,
} from "@mui/icons-material";
import { useState, useEffect } from "react";
import { keyframes } from "@mui/system";
import apiEndpoints from "../apiconfig";

// Floating animation
const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-3px); }
  100% { transform: translateY(0px); }
`;

const Activities = () => {
  const [filter, setFilter] = useState("all");
  const [expandedActivity, setExpandedActivity] = useState(null);
  const [bookmarked, setBookmarked] = useState([]);
  const [liked, setLiked] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem("parent_token");
  const raw = localStorage.getItem("active_student");

  const activityCategories = [
    {
      id: "art",
      label: "Art & Creativity",
      color: "#FF6B6B",
      icon: <Palette />,
    },
    { id: "music", label: "Music", color: "#4ECDC4", icon: <MusicNote /> },
    { id: "sports", label: "Sports", color: "#FFD166", icon: <SportsSoccer /> },
    { id: "science", label: "Science", color: "#06D6A0", icon: <Science /> },
    {
      id: "practical",
      label: "Practical Life",
      color: "#7209B7",
      icon: <LocalLibrary />,
    },
    {
      id: "academic",
      label: "Academic",
      color: "#4895EF",
      icon: <TrendingUp />,
    },
    { id: "social", label: "Social Skills", color: "#F72585", icon: <Group /> },
  ];

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        if (!token) return;
        const student_guid = raw ? JSON.parse(raw).guid : null;
        const res = await fetch(
          `${apiEndpoints.activity}?student_guid=${student_guid}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await res.json();

        if (data.status) {
          setActivities(
            data.activities.map((a) => ({
              id: a.guid,
              title: a.title,
              date: a.activity_date,
              category: "academic", // until you store category in DB
              photos: a.images || [],
              notes: a.description,
              teacher: a.teacher_name || "Staff Member",
              location: "",
              skills: a.skills || [],
              mood: "Engaged",
              engagement: Number(a.rating) || 0,
              likes: 0,
              views: 0,
              comments: 0,
              attachments: [],
            }))
          );
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, []);

  const handleExpand = (id) => {
    setExpandedActivity(expandedActivity === id ? null : id);
  };

  const handleBookmark = (id) => {
    setBookmarked((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleLike = (id) => {
    setLiked((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const filteredActivities =
    filter === "all"
      ? activities
      : activities.filter((activity) => activity.category === filter);

  const getCategory = (id) => activityCategories.find((c) => c.id === id);

  const getMoodIcon = (mood) => {
    switch (mood.toLowerCase()) {
      case "happy":
        return <EmojiEmotions sx={{ color: "#FFD700" }} />;
      case "energetic":
        return <EmojiEmotions sx={{ color: "#FF6B6B" }} />;
      case "curious":
        return <Psychology sx={{ color: "#4ECDC4" }} />;
      case "joyful":
        return <EmojiEmotions sx={{ color: "#FFB300" }} />;
      case "focused":
        return <TrendingUp sx={{ color: "#1976D2" }} />;
      case "engaged":
        return <Grade sx={{ color: "#4CAF50" }} />;
      default:
        return <EmojiEmotions sx={{ color: "#666" }} />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        backgroundImage: `
          linear-gradient(rgba(255, 255, 255, 0.92), rgba(255, 255, 255, 0.92)),
          url('https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=1920')
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.1) 0%, transparent 50%)
          `,
          pointerEvents: "none",
        },
      }}
    >
      {/* Decorative elements */}
      <Box
        sx={{
          position: "absolute",
          top: 100,
          right: 100,
          width: 200,
          height: 200,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(120, 119, 198, 0.05) 0%, transparent 70%)",
          filter: "blur(40px)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: 100,
          left: 100,
          width: 150,
          height: 150,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255, 119, 198, 0.05) 0%, transparent 70%)",
          filter: "blur(30px)",
        }}
      />

      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
        {/* HEADER */}
        <Fade in timeout={800}>
          <Box sx={{ py: 4, px: { xs: 2, md: 4 } }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: "#333",
                    mb: 1,
                    fontSize: { xs: "1.75rem", md: "2.125rem" },
                  }}
                >
                  Daily Activities
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "text.secondary", fontWeight: 400 }}
                >
                  View your child's daily learning experiences, photos, and
                  teacher observations
                </Typography>
              </Box>
            </Box>
          </Box>
        </Fade>

        {/* QUICK STATS */}
        <Fade in timeout={900}>
          <Grid container spacing={3} sx={{ mb: 4, px: { xs: 2, md: 4 } }}>
            <Grid
              item
              sx={{
                width: {
                  xs: "100%",
                  sm: "50%",
                  md: "50%",
                  lg: "25%",
                  xl: "25%",
                },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  background: "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="h3"
                  sx={{ fontWeight: 900, color: "#FF6B6B", mb: 1 }}
                >
                  {activities.length}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#666", fontWeight: 500 }}
                >
                  Total Activities
                </Typography>
              </Paper>
            </Grid>
            <Grid
              item
              sx={{
                width: {
                  xs: "100%",
                  sm: "50%",
                  md: "50%",
                  lg: "25%",
                  xl: "25%",
                },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  background: "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="h3"
                  sx={{ fontWeight: 900, color: "#4ECDC4", mb: 1 }}
                >
                  {activities.reduce(
                    (acc, activity) => acc + activity.photos.length,
                    0
                  )}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#666", fontWeight: 500 }}
                >
                  Photos Shared
                </Typography>
              </Paper>
            </Grid>
            <Grid
              item
              sx={{
                width: {
                  xs: "100%",
                  sm: "50%",
                  md: "50%",
                  lg: "25%",
                  xl: "25%",
                },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  background: "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="h3"
                  sx={{ fontWeight: 900, color: "#FFD166", mb: 1 }}
                >
                  {activities.reduce(
                    (acc, activity) => acc + activity.skills.length,
                    0
                  )}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#666", fontWeight: 500 }}
                >
                  Skills Developed
                </Typography>
              </Paper>
            </Grid>
            <Grid
              item
              sx={{
                width: {
                  xs: "100%",
                  sm: "50%",
                  md: "50%",
                  lg: "25%",
                  xl: "25%",
                },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  background: "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="h3"
                  sx={{ fontWeight: 900, color: "#06D6A0", mb: 1 }}
                >
                  {activities.reduce(
                    (acc, activity) => acc + activity.likes,
                    0
                  )}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#666", fontWeight: 500 }}
                >
                  Total Likes
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Fade>

        {/* CATEGORY FILTER */}
        <Fade in timeout={1000}>
          <Paper
            elevation={0}
            sx={{
              mb: 4,
              mx: { xs: 2, md: 4 },
              p: 3,
              borderRadius: "20px",
              background: "rgba(255, 255, 255, 0.7)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              boxShadow: "0 8px 32px rgba(31, 38, 135, 0.1)",
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, mb: 2, color: "#666" }}
            >
              Filter by Activity Type:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <Chip
                label="All Activities"
                clickable
                onClick={() => setFilter("all")}
                variant={filter === "all" ? "filled" : "outlined"}
                sx={{
                  bgcolor:
                    filter === "all" ? alpha("#1976D2", 0.1) : "transparent",
                  color: filter === "all" ? "#1976D2" : "#666",
                  borderColor: alpha("#1976D2", 0.3),
                  fontWeight: 500,
                }}
              />
              {activityCategories.map((category) => (
                <Chip
                  key={category.id}
                  icon={category.icon}
                  label={category.label}
                  clickable
                  onClick={() => setFilter(category.id)}
                  variant={filter === category.id ? "filled" : "outlined"}
                  sx={{
                    bgcolor:
                      filter === category.id
                        ? alpha(category.color, 0.1)
                        : "transparent",
                    color: filter === category.id ? category.color : "#666",
                    borderColor: alpha(category.color, 0.3),
                    fontWeight: 500,
                    "&:hover": {
                      bgcolor: alpha(category.color, 0.1),
                    },
                  }}
                />
              ))}
            </Box>
          </Paper>
        </Fade>

        {/* ACTIVITIES LIST */}
        <Fade in timeout={1100}>
          <Box sx={{ px: { xs: 2, md: 4 } }}>
            <Grid container spacing={3}>
              {filteredActivities.map((activity) => {
                const category = getCategory(activity.category);

                return (
                  <Grid item xs={12} key={activity.id}>
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: "20px",
                        background: "rgba(255, 255, 255, 0.7)",
                        backdropFilter: "blur(20px)",
                        border: `1px solid ${alpha(category.color, 0.2)}`,
                        overflow: "hidden",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow: "0 12px 40px rgba(0, 0, 0, 0.08)",
                          transform: "translateY(-4px)",
                        },
                      }}
                    >
                      {/* Activity Header */}
                      <Box
                        sx={{
                          p: 3,
                          bgcolor: alpha(category.color, 0.05),
                          borderBottom: `1px solid ${alpha(
                            category.color,
                            0.1
                          )}`,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            flexWrap: "wrap",

                            alignItems: "flex-start",
                            mb: 2,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",

                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <Box
                              sx={{
                                width: 56,
                                height: 56,
                                borderRadius: "14px",
                                bgcolor: alpha(category.color, 0.1),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: category.color,
                                animation: `${floatAnimation} 2s ease-in-out infinite`,
                              }}
                            >
                              {category.icon}
                            </Box>
                            <Box>
                              <Typography
                                variant="h5"
                                sx={{
                                  fontWeight: 700,
                                  color: "#1a1a1a",
                                  mb: 0.5,
                                }}
                              >
                                {activity.title}
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                }}
                              >
                                <Chip
                                  icon={category.icon}
                                  label={category.label}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(category.color, 0.1),
                                    color: category.color,
                                    fontWeight: 600,
                                  }}
                                />
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  {getMoodIcon(activity.mood)}
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "#666" }}
                                  >
                                    {activity.mood}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                          {/* <Box sx={{ display: "flex", gap: 1 }}>
                            <IconButton
                              onClick={() => handleExpand(activity.id)}
                            >
                              {expandedActivity === activity.id ? (
                                <ExpandLess />
                              ) : (
                                <ExpandMore />
                              )}
                            </IconButton>
                          </Box> */}
                        </Box>

                        {/* Quick Info */}
                        <Grid container spacing={2}>
                          <Grid item xs={6} md={3}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <CalendarToday
                                sx={{ fontSize: 16, color: "#666" }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ color: "#666" }}
                              >
                                {formatDate(activity.date)}
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item xs={6} md={3}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Person sx={{ fontSize: 16, color: "#666" }} />
                              <Typography
                                variant="body2"
                                sx={{ color: "#666" }}
                              >
                                {activity.teacher}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>

                      {/* Photos Gallery */}
                      <Box sx={{ p: 3 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            mb: 2,
                            color: "#333",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <PhotoCamera /> Photos
                        </Typography>
                        <Grid container spacing={2}>
                          {activity.photos.map((photo, index) => (
                            <Grid
                              item
                              sx={{
                                width: {
                                  xs: "100%",
                                  sm: "90%",
                                  md: "80%",
                                  lg: "47%",
                                  xl: "48%",
                                },
                              }}
                              key={index}
                            >
                              <Card
                                sx={{
                                  borderRadius: "12px",
                                  overflow: "hidden",
                                  position: "relative",
                                  cursor: "pointer",
                                  transition: "all 0.3s ease",
                                  "&:hover": {
                                    transform: "scale(1.02)",
                                    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                                  },
                                }}
                              >
                                <CardMedia
                                  component="img"
                                  height="200"
                                  image={photo}
                                  alt={`Activity photo ${index + 1}`}
                                  sx={{
                                    objectFit: "cover",
                                    transition: "transform 0.3s ease",
                                    "&:hover": {
                                      transform: "scale(1.1)",
                                    },
                                  }}
                                />
                                <CardActions
                                  sx={{
                                    position: "absolute",
                                    bottom: 0,
                                    right: 0,
                                    p: 1,
                                  }}
                                >
                                  <Tooltip title="Download">
                                    <IconButton
                                      size="small"
                                      sx={{ bgcolor: "rgba(255,255,255,0.9)" }}
                                    >
                                      <Download fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </CardActions>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>

                      {/* Observation Notes */}
                      <Box sx={{ p: 3, pt: 0 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            mb: 2,
                            color: "#333",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Description /> Observation Notes
                        </Typography>
                        <Paper
                          sx={{
                            p: 3,
                            borderRadius: "12px",
                            bgcolor: "rgba(0, 0, 0, 0.02)",
                            border: "1px solid rgba(0, 0, 0, 0.05)",
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{ color: "#444", lineHeight: 1.7, mb: 2 }}
                          >
                            {activity.notes}
                          </Typography>

                          <Divider sx={{ my: 2 }} />

                          {/* Skills Developed */}
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 600, mb: 1, color: "#666" }}
                            >
                              Skills Developed:
                            </Typography>
                            <Box
                              sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
                            >
                              {activity.skills.map((skill, index) => (
                                <Chip
                                  key={index}
                                  label={skill}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha("#4CAF50", 0.1),
                                    color: "#4CAF50",
                                    fontWeight: 500,
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>

                          {/* Engagement Rating */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: 2,
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 600, color: "#666" }}
                            >
                              Engagement Level:
                            </Typography>
                            <Rating
                              value={activity.engagement}
                              max={5}
                              readOnly
                            />
                            <Typography
                              variant="caption"
                              sx={{ color: "#666" }}
                            >
                              ({activity.engagement}/5)
                            </Typography>
                          </Box>
                        </Paper>
                      </Box>

                      {/* Collapsible Additional Details */}
                      <Collapse in={expandedActivity === activity.id}>
                        <Box sx={{ p: 3, pt: 0 }}>
                          {/* Attachments */}
                          {activity.attachments.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                              <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 600, mb: 2, color: "#333" }}
                              >
                                📎 Attachments
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 1,
                                }}
                              >
                                {activity.attachments.map((file, index) => (
                                  <Chip
                                    key={index}
                                    icon={<Description />}
                                    label={file}
                                    size="small"
                                    onClick={() => {
                                      /* Handle download */
                                    }}
                                    sx={{
                                      bgcolor: "rgba(0, 0, 0, 0.04)",
                                      cursor: "pointer",
                                      "&:hover": {
                                        bgcolor: "rgba(0, 0, 0, 0.08)",
                                      },
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Collapse>

                      {/* Activity Footer */}
                      {/* <Box
                        sx={{
                          p: 2,
                          bgcolor: "rgba(0, 0, 0, 0.02)",
                          borderTop: "1px solid rgba(0, 0, 0, 0.05)",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Box sx={{ display: "flex", gap: 3 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => handleLike(activity.id)}
                                sx={{
                                  color: liked.includes(activity.id)
                                    ? "#FF6B6B"
                                    : "#666",
                                }}
                              >
                                <ThumbUp fontSize="small" />
                              </IconButton>
                              <Typography
                                variant="caption"
                                sx={{ color: "#666" }}
                              >
                                {activity.likes +
                                  (liked.includes(activity.id) ? 1 : 0)}{" "}
                                likes
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Visibility
                                sx={{ fontSize: 16, color: "#666" }}
                              />
                              <Typography
                                variant="caption"
                                sx={{ color: "#666" }}
                              >
                                {activity.views} views
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Comment sx={{ fontSize: 16, color: "#666" }} />
                              <Typography
                                variant="caption"
                                sx={{ color: "#666" }}
                              >
                                {activity.comments} comments
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Button size="small" startIcon={<Share />}>
                              Share
                            </Button>
                            <Button size="small" startIcon={<Download />}>
                              Save
                            </Button>
                          </Box>
                        </Box>
                      </Box> */}
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>

            {/* Empty State */}
            {filteredActivities.length === 0 && (
              <Paper
                elevation={0}
                sx={{
                  p: 8,
                  borderRadius: "20px",
                  background: "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  textAlign: "center",
                }}
              >
                <PhotoCamera sx={{ fontSize: 60, color: "#ccc", mb: 2 }} />
                <Typography variant="h6" sx={{ color: "#666", mb: 1 }}>
                  No activities found for this category
                </Typography>
                <Typography variant="body2" sx={{ color: "#888" }}>
                  Try selecting a different category or check back later for
                  updates
                </Typography>
              </Paper>
            )}
          </Box>
        </Fade>

        {/* FOOTER */}
        <Fade in timeout={1200}>
          <Box sx={{ py: 4, textAlign: "center", px: { xs: 2, md: 4 } }}>
            <Typography variant="body2" sx={{ color: "#666", opacity: 0.8 }}>
              © 2025 My School • Activities are updated daily by teachers • Last
              updated: Today, 3:15 PM
            </Typography>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default Activities;
